/**
 * «Enviar por correo» (pedido de la dueña 2026-10-19; decisión «Correo con Resend: la única integración autorizada»).
 * Envía la factura, la nota, la nota de crédito o débito, el cambio, la cotización, el recibo de abono o el pago de un
 * reintegro al correo REGISTRADO del cliente, con el formato ya armado. No se escribe otra dirección: la pantalla
 * muestra a cuál va y pide confirmar. Deshabilitado, con su motivo a la vista, si el servidor no tiene el correo
 * configurado o si el cliente no tiene correo (o es consumidor final, o el documento está anulado).
 *
 * En una fila de tabla (`enFila`) no se pregunta a quién va hasta abrir la ventana: así una lista larga no hace una
 * consulta por fila. La ventana dice el motivo si no se puede enviar.
 * Solo sale a quien puede ENVIARLO: el permiso de eso completo (no en lectura) y no el contador, que solo ve lo fiscal
 * (revisión de seguridad 2026-10-19; `puedeEnviarCorreo`). El servidor decide igual.
 *
 * Tope del día (pedido del usuario 2026-11-02): a un cliente le salen a lo sumo 3 correos por día. La ventana dice
 * cuántos más puede recibir hoy y, al enviar, el aviso dice cuántos le quedan. Con el tope cumplido, ese es el motivo.
 */
import {
  CORREOS_CLIENTE_POR_DIA,
  correosRestantesTexto,
  MENSAJE_CORREO_NO_CONFIGURADO,
  PERMISO_CORREO,
  ROLES_SIN_ENVIO_CORREO,
  type CorreoDestinatarioDTO,
  type CorreoEnviadoDTO,
  type CorreoEstadoDTO,
  type TipoCorreo,
} from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { get, mensajeDeError, post } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { cn } from '@/lib/utils.ts';

export const clavesCorreo = {
  estado: ['correo', 'estado'] as const,
  destinatario: (tipo: TipoCorreo, id: string) => ['correo', 'destinatario', tipo, id] as const,
};

/** ¿El servidor tiene la clave de Resend y el remitente? (se pregunta una vez y se recuerda unos minutos) */
export const useEstadoCorreo = (habilitado = true) => useQuery({ queryKey: clavesCorreo.estado, queryFn: () => get<CorreoEstadoDTO>('/correo/estado'), enabled: habilitado, staleTime: 5 * 60_000 });

/** Después de enviar: cuántos correos le quedan hoy a esa dirección (null en la orden de compra al proveedor). */
export const textoRestantesHoy = (n: number | null | undefined): string | null =>
  typeof n !== 'number' ? null : n === 0 ? `Hoy ya no se le pueden enviar más correos (máximo ${CORREOS_CLIENTE_POR_DIA} por día)` : `Hoy le quedan ${correosRestantesTexto(n)} más (máximo ${CORREOS_CLIENTE_POR_DIA} por día)`;

/** Antes de enviar: cuántos puede recibir hoy, contando este. */
const textoCupoHoy = (n: number) =>
  n === 1 ? `Es el último correo que puede recibir hoy (máximo ${CORREOS_CLIENTE_POR_DIA} por día).` : `Hoy puede recibir ${correosRestantesTexto(n)} más (máximo ${CORREOS_CLIENTE_POR_DIA} por día).`;

const QUE: Record<TipoCorreo, string> = { documento: 'el documento', cotizacion: 'la cotización', abono: 'el recibo de abono', reintegro: 'el pago del reintegro', orden_compra: 'la orden de compra', retiro: 'el estado del retiro: lo comprado, lo retirado y lo pendiente, sin precios' };

interface Props {
  tipo: TipoCorreo;
  id: string;
  /** En una fila de tabla: botón pequeño y sin consultar el destinatario hasta abrir la ventana. */
  enFila?: boolean;
  className?: string;
  /** Texto del botón (por defecto «Enviar por correo»; el estado del retiro dice «Enviar estado de retiro»). */
  etiqueta?: string;
}

export function EnviarCorreo({ tipo, id, enFila = false, className, etiqueta = 'Enviar por correo' }: Props) {
  const { puede, usuario } = useAuth();
  // La misma regla que `puedeEnviarCorreo` del servidor, con los permisos efectivos de la sesión
  const permitido = !!usuario && !ROLES_SIN_ENVIO_CORREO.includes(usuario.rol) && puede(PERMISO_CORREO[tipo], 'total');
  const [abierto, setAbierto] = useState(false);
  const qc = useQueryClient();
  const estado = useEstadoCorreo(permitido);
  const configurado = estado.data?.configurado === true;
  const destino = useQuery({
    queryKey: clavesCorreo.destinatario(tipo, id),
    queryFn: () => get<CorreoDestinatarioDTO>('/correo/destinatario', { tipo, id }),
    enabled: permitido && configurado && !!id && (!enFila || abierto),
    staleTime: 30_000,
  });
  const enviar = useMutation({
    mutationFn: () => post<CorreoEnviadoDTO>('/correo/enviar', { tipo, id }),
    onSuccess: (r) => {
      const restantes = textoRestantesHoy(r.restantes_hoy);
      toast.success(`Correo enviado a ${r.para}`, {
        description: (
          <>
            <span className="block">{r.asunto}</span>
            {restantes ? <span className="block font-medium" data-testid="correo-restantes-hoy">{restantes}</span> : null}
          </>
        ),
      });
      setAbierto(false);
      // El cupo del día es de la dirección: cualquier otro documento de ese cliente ya lo ve
      void qc.invalidateQueries({ queryKey: ['correo', 'destinatario'] });
    },
    onError: (e) => toast.error('No se pudo enviar el correo', { description: mensajeDeError(e) }),
  });
  if (!permitido) return null;

  // Por qué no se puede: primero el servidor sin configurar; después lo que diga el destinatario (sin correo…)
  const motivo = estado.error ? mensajeDeError(estado.error) : estado.data && !configurado ? MENSAJE_CORREO_NO_CONFIGURADO : (destino.data?.motivo ?? (destino.error ? mensajeDeError(destino.error) : null));
  const listoParaAbrir = configurado && (enFila || (!!destino.data && !destino.data.motivo));
  const para = destino.data?.para ?? null;

  return (
    <div className={cn('no-print inline-flex flex-col items-start gap-0.5', className)} data-testid={`enviar-correo-${tipo}`}>
      <Button
        variant="outline"
        size={enFila ? 'sm' : 'default'}
        disabled={!listoParaAbrir}
        cargando={estado.isLoading || (!enFila && configurado && destino.isLoading)}
        onClick={() => setAbierto(true)}
        title={para ? `Enviar a ${para}` : (motivo ?? undefined)}
        data-testid={tipo === 'retiro' ? 'enviar-correo-retiro' : 'enviar-correo'}
      >
        <Mail /> {etiqueta}
      </Button>
      {/* El motivo a la vista: un botón deshabilitado no muestra su «title» al pasar el ratón */}
      {motivo && !enFila ? <span className="max-w-xs text-[11px] leading-tight text-muted-foreground" data-testid="enviar-correo-motivo">{motivo}</span> : null}
      {/* En la fila, corto (el texto completo al pasar el ratón): se repetiría en cada fila */}
      {motivo && enFila && !configurado ? <span className="text-[11px] leading-tight text-muted-foreground" title={motivo} data-testid="enviar-correo-motivo">Correo no configurado</span> : null}
      <Dialog open={abierto} onOpenChange={(v) => (enviar.isPending ? undefined : setAbierto(v))}>
        <DialogContent data-testid="enviar-correo-dialogo">
          <DialogHeader>
            <DialogTitle>{etiqueta}</DialogTitle>
            <DialogDescription>{destino.data?.asunto ?? `Se envía ${QUE[tipo]} con su formato.`}</DialogDescription>
          </DialogHeader>
          {destino.isLoading ? <p className="text-sm text-muted-foreground">{tipo === 'orden_compra' ? 'Buscando el correo del proveedor…' : 'Buscando el correo del cliente…'}</p> : null}
          {para ? (
            <p className="text-sm" data-testid="enviar-correo-para">
              Se envía a <b>{para}</b> (correo registrado de {destino.data?.cliente}).{tipo === 'reintegro' ? ' Va con el comprobante del pago que esté adjunto.' : ''}
            </p>
          ) : null}
          {para && typeof destino.data?.restantes_hoy === 'number' ? (
            <p className="text-xs text-muted-foreground" data-testid="enviar-correo-cupo">
              {textoCupoHoy(destino.data.restantes_hoy)}
            </p>
          ) : null}
          {motivo && abierto ? <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="enviar-correo-dialogo-motivo">{motivo}</p> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbierto(false)} disabled={enviar.isPending}>Cancelar</Button>
            <Button disabled={!para || !!motivo} cargando={enviar.isPending} onClick={() => enviar.mutate()} data-testid="enviar-correo-confirmar">
              <Mail /> Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
